import { standaloneInputClassName } from "@/components/podkit/forms/input-classes";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Combobox, ComboboxItem, ComboboxList, ComboboxProvider } from "@ariakit/react";
import * as RadixPopover from "@radix-ui/react-popover";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";
import type React from "react";
import { useCallback, useRef, useState } from "react";

type Props = {
    id?: string;
    autoFocus?: boolean;
    disabled?: boolean;
    name?: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    suggestions: string[];
    value?: string;
    error?: string;
    /** Whether to reserve space for error message when no error is present */
    reserveErrorSpace?: boolean;
    "data-testid"?: string;
    "data-track-location": string;
} & PropsWithClassName;

const InputWithSuggestions: React.FC<Props> = ({
    id,
    autoFocus,
    disabled,
    name,
    onChange,
    onBlur,
    placeholder,
    suggestions,
    value,
    error,
    reserveErrorSpace = true,
    "data-testid": testId,
    "data-track-location": dataTrackLocation,
}) => {
    const updateValue = (value: string) => {
        if (onChange) {
            onChange(value);
        }
    };

    const filteredSuggestions = suggestions.filter(
        (item) => !value || item.toLowerCase().includes(value.toLowerCase()),
    );
    const hasSuggestions = filteredSuggestions.length > 0;

    const handleBlur = useCallback(() => {
        if (onBlur) {
            onBlur();
        }
    }, [onBlur]);

    const comboboxRef = useRef<HTMLInputElement>(null);
    const listboxRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);

    return (
        <RadixPopover.Root open={open} onOpenChange={setOpen} modal data-track-location={dataTrackLocation}>
            <ComboboxProvider setValue={updateValue} value={value} open={open} setOpen={setOpen}>
                <RadixPopover.Anchor asChild>
                    <Combobox
                        id={id}
                        placeholder={placeholder}
                        className={cn(standaloneInputClassName, {
                            "border-border-error focus-visible:border-border-light": error,
                        })}
                        autoFocus={autoFocus}
                        disabled={disabled}
                        name={name}
                        onBlur={handleBlur}
                        data-testid={testId}
                        ref={comboboxRef}
                    />
                </RadixPopover.Anchor>
                {hasSuggestions && (
                    <RadixPopover.Portal>
                        <RadixPopover.Content
                            asChild
                            sideOffset={8}
                            onOpenAutoFocus={(event) => event.preventDefault()}
                            onInteractOutside={(event) => {
                                const target = event.target as Element | null;
                                const isCombobox = target === comboboxRef.current;
                                const inListbox = target && listboxRef.current?.contains(target);
                                if (isCombobox || inListbox) {
                                    event.preventDefault();
                                }
                            }}
                        >
                            <ScrollAreaPrimitive.Root className="z-50 rounded-lg border border-border-base" type="auto">
                                <ScrollAreaPrimitive.Viewport
                                    className={cn(
                                        "rounded-lg",
                                        "max-h-[min(320px,calc(var(--radix-popper-available-height)-61px))]",
                                        "w-[var(--radix-popover-trigger-width)] min-w-32",
                                    )}
                                >
                                    <ComboboxList
                                        ref={listboxRef}
                                        role="listbox"
                                        className={cn(
                                            "bg-surface-popover text-content-primary shadow-md",
                                            "focus:ring-0",
                                            "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
                                        )}
                                    >
                                        {filteredSuggestions.map((value) => (
                                            <ComboboxItem
                                                key={value}
                                                value={value}
                                                className={cn(
                                                    "cursor-pointer px-3 py-2 text-base opacity-100",
                                                    "hover:bg-surface-hover",
                                                    "data-[active-item=true]:bg-surface-hover data-[active-item=true]:text-content-primary data-[active-item=true]:ring-0",
                                                )}
                                            />
                                        ))}
                                    </ComboboxList>
                                </ScrollAreaPrimitive.Viewport>
                                <ScrollAreaPrimitive.Scrollbar className="p-0.5" orientation="vertical">
                                    <ScrollAreaPrimitive.Thumb
                                        className="size-2 rounded-lg bg-surface-tertiary"
                                        style={{ "--radix-scroll-area-thumb-width": "8px" } as React.CSSProperties}
                                    />
                                </ScrollAreaPrimitive.Scrollbar>
                            </ScrollAreaPrimitive.Root>
                        </RadixPopover.Content>
                    </RadixPopover.Portal>
                )}
                <div className={cn("text-sm text-content-negative", { "min-h-4": reserveErrorSpace || error })}>
                    {error ?? ""}
                </div>
            </ComboboxProvider>
        </RadixPopover.Root>
    );
};

export default InputWithSuggestions;
